import { type ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, type UseFormRegisterReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { authApi, type LoginResponse } from '@/core/auth/authApi';
import { useAuthStore } from '@/core/auth/authStore';
import { decodeJwtClaims } from '@/core/auth/jwt';
import { getAccessToken } from '@/core/api/client';
import { isWebAllowedRole } from '@/core/authz/permissions';
import { ApiError } from '@/core/api/envelope';
import { normalizeIdentifier, looksLikeBarePhone } from '@/core/auth/identifier';

type Mode = 'password' | 'otp';

export function LoginPage() {
  const [mode, setMode] = useState<Mode>('password');
  return (
    <div className="grid h-full place-items-center bg-fv-ink p-6">
      <div className="w-full max-w-md rounded-panel bg-white p-8 shadow-lg">
        <h1 className="text-2xl font-bold text-fv-text">
          Food Valve <span className="text-fv-primary">Admin</span>
        </h1>
        <p className="mt-1 text-sm text-fv-muted">Sign in to the platform console</p>
        <div className="mt-6 flex rounded-lg bg-fv-surfaceLow p-1 text-sm font-semibold">
          <TabBtn active={mode === 'password'} onClick={() => setMode('password')}>
            Password
          </TabBtn>
          <TabBtn active={mode === 'otp'} onClick={() => setMode('otp')}>
            OTP
          </TabBtn>
        </div>
        <div className="mt-6">{mode === 'password' ? <PasswordForm /> : <OtpForm />}</div>
      </div>
    </div>
  );
}

function useLoginSuccess() {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  return async (login?: LoginResponse) => {
    const perms = await authApi.permissions();
    if (!isWebAllowedRole(perms.role)) {
      navigate('/access-denied', { replace: true });
      return;
    }
    // Identity comes from the login response's `user`; perms.role is authoritative.
    // Fall back to the access-token `sub` for the id if the response omits it.
    const u = login?.user;
    const id = u?.id ?? decodeJwtClaims(getAccessToken())?.sub;
    setSession({
      profile: { id, email: u?.email, role: perms.role },
      permissions: perms.permissions,
      mustChangePassword: perms.must_change_password,
    });
    navigate(perms.must_change_password ? '/force-change-password' : '/dashboard', {
      replace: true,
    });
  };
}

const passwordSchema = z.object({
  identifier: z.string().min(3, 'Enter your email or phone'),
  password: z.string().min(1, 'Enter your password'),
});
type PasswordValues = z.infer<typeof passwordSchema>;

function PasswordForm() {
  const onSuccess = useLoginSuccess();
  const [err, setErr] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<PasswordValues>({ resolver: zodResolver(passwordSchema) });

  const submit = handleSubmit(async (v) => {
    setErr(null);
    try {
      await authApi.csrf();
      const login = await authApi.loginPassword(normalizeIdentifier(v.identifier), v.password);
      await onSuccess(login);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Login failed');
    }
  });

  return (
    <form onSubmit={submit} className="space-y-4">
      <Field label="Email or phone" error={errors.identifier?.message}>
        <IdentifierInput registerProps={register('identifier')} value={watch('identifier') ?? ''} />
      </Field>
      <Field label="Password" error={errors.password?.message}>
        <input type="password" className="fv-input" autoComplete="current-password" {...register('password')} />
      </Field>
      {err && <p className="text-sm text-fv-error">{err}</p>}
      <SubmitBtn loading={isSubmitting}>Sign in</SubmitBtn>
    </form>
  );
}

const otpRequestSchema = z.object({ identifier: z.string().min(3, 'Enter your email or phone') });
const otpVerifySchema = z.object({ otp: z.string().min(4, 'Enter the code') });
type OtpRequestValues = z.infer<typeof otpRequestSchema>;
type OtpVerifyValues = z.infer<typeof otpVerifySchema>;

function OtpForm() {
  const onSuccess = useLoginSuccess();
  const [identifier, setIdentifier] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const reqForm = useForm<OtpRequestValues>({ resolver: zodResolver(otpRequestSchema) });
  const verForm = useForm<OtpVerifyValues>({ resolver: zodResolver(otpVerifySchema) });

  const requestOtp = reqForm.handleSubmit(async (v) => {
    setErr(null);
    const normalized = normalizeIdentifier(v.identifier);
    try {
      await authApi.csrf();
      await authApi.requestOtp(normalized);
      setIdentifier(normalized);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not send code');
    }
  });

  const verifyOtp = verForm.handleSubmit(async (v) => {
    setErr(null);
    try {
      const login = await authApi.verifyOtp(identifier ?? '', v.otp);
      await onSuccess(login);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Invalid code');
    }
  });

  if (identifier === null) {
    return (
      <form onSubmit={requestOtp} className="space-y-4">
        <Field label="Email or phone" error={reqForm.formState.errors.identifier?.message}>
          <IdentifierInput
            registerProps={reqForm.register('identifier')}
            value={reqForm.watch('identifier') ?? ''}
          />
        </Field>
        {err && <p className="text-sm text-fv-error">{err}</p>}
        <SubmitBtn loading={reqForm.formState.isSubmitting}>Send code</SubmitBtn>
      </form>
    );
  }

  return (
    <form onSubmit={verifyOtp} className="space-y-4">
      <p className="text-sm text-fv-muted">Code sent to {identifier}</p>
      <Field label="Verification code" error={verForm.formState.errors.otp?.message}>
        <input className="fv-input tracking-widest" {...verForm.register('otp')} />
      </Field>
      {err && <p className="text-sm text-fv-error">{err}</p>}
      <SubmitBtn loading={verForm.formState.isSubmitting}>Verify &amp; sign in</SubmitBtn>
    </form>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active ? 'flex-1 rounded-md bg-white py-1.5 text-fv-primary shadow-sm' : 'flex-1 rounded-md py-1.5 text-fv-muted'
      }
    >
      {children}
    </button>
  );
}

/** Shared "Email or phone" input. Once the user types a bare digit-only value
 *  (no '@', no leading '+'), shows a static "+91" prefix chip so it reads
 *  the same way a dedicated phone input would — without a separate mode
 *  toggle, since this one field still accepts email too. */
function IdentifierInput({ registerProps, value }: { registerProps: UseFormRegisterReturn; value: string }) {
  const showPrefix = looksLikeBarePhone(value);
  return (
    <div className="relative">
      {showPrefix && (
        <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-fv-muted">
          +91
        </span>
      )}
      <input
        className={showPrefix ? 'fv-input pl-10' : 'fv-input'}
        autoComplete="username"
        placeholder="you@company.com or 10-digit phone"
        {...registerProps}
      />
    </div>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-fv-text">{label}</span>
      {children}
      {error && <span className="mt-1 block text-xs text-fv-error">{error}</span>}
    </label>
  );
}

function SubmitBtn({ loading, children }: { loading: boolean; children: ReactNode }) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="w-full rounded-lg bg-fv-primary py-2.5 font-semibold text-white hover:bg-fv-primaryDark disabled:opacity-60"
    >
      {loading ? 'Please wait…' : children}
    </button>
  );
}
